import { Component, inject, PLATFORM_ID, ChangeDetectionStrategy, AfterViewInit } from '@angular/core'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { environment } from '@environments/environment'
import { SuccessMessageComponent } from '@shared/components/success-message/success-message.component'
import { ErrorMessageComponent } from '@shared/components/error-message/error-message.component'
import { filter } from 'rxjs/operators'
import { BrowserLocalStorageService } from '@shared/services/browser-local-storage.service'
import { CustomerNavigationService } from '@shared/services/customer-navigation.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import { ServiceNotificationsComponent } from '@shared/components/service-notifications/service-notifications.component'
import { AffiliatesService } from '@shared/services/affiliates.service'

declare global {
  interface Window {
    dataLayer: any[]
  }
}

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, SuccessMessageComponent, ErrorMessageComponent, ServiceNotificationsComponent],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements AfterViewInit {
  environment = environment

  private router = inject(Router)
  private localStorage = inject(BrowserLocalStorageService)
  private customerNavigationService = inject(CustomerNavigationService)
  private platformId = inject(PLATFORM_ID)
  private affiliatesService = inject(AffiliatesService)
  private document = inject<Document>(DOCUMENT)

  constructor() {
    this.customerNavigationService.init()

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e: any) => {
        const raw = (e.urlAfterRedirects || '').split('?')[0].split('#')[0]
        const path = raw.split(';')[0].replace(/\/+$/, '') || '/'

        const map: Record<string, string> = {
          '/loans/credit-card': 'first_page',
          '/continue': 'first_page',
          '/customer-details': 'customer_details',
          '/extra-details': 'extra_details',
          '/transactions-check': 'kreditz_check',
          '/credit-card': 'offer',
          '/terms-and-conditions': 'terms',
          '/bank-authorization': 'signing_terms',
          '/card-password': 'password',
        }

        const step = map[path]
        if (!step) {
          return
        }

        if (step === 'first_page') {
          this.localStorage.keys().forEach(k => {
            if (k.startsWith('flow_step_')) {
              this.localStorage.remove(k)
            }
          })
          this.localStorage.remove('current_loan_id')
          this.localStorage.remove('broker_name')
        }

        const effectiveLoanId = this.localStorage.get('current_loan_id') || null
        const brokerName = this.localStorage.get('broker_name') || null

        const dedupeKey = effectiveLoanId ? `flow_step_${effectiveLoanId}_${step}` : `flow_step_preloan_${step}`

        if (this.localStorage.get(dedupeKey)) {
          return
        }
        this.localStorage.set(dedupeKey, '1')

        if (isPlatformBrowser(this.platformId)) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            broker_name: brokerName || '(direct)',
            event: 'application_step',
            step,
            loan_id: effectiveLoanId || undefined,
            page_path: path,
          })
        }
      })
  }

  ngAfterViewInit() {
    this.document.body.classList.remove('theme-dark')
    // Also parse initial URL on first render
    if (isPlatformBrowser(this.platformId)) {
      try {
        this.affiliatesService.handleQueryString(window.location.search || null)
      } catch (error) {
        console.error(error)
      }
    }
  }
}
